import { useRef } from 'react';
import PropTypes from 'prop-types';
import { Select, SelectContent, SelectItem } from '@dextech-ai/dex-design-system';
import { StatusBadge } from './status-badge';

// Stable id seed for instances that aren't given a testId (React 17 has no useId).
let instanceSeq = 0;

// Version label followed by its status badge (used for the options and the picked value).
function VersionLabel({ option }) {
	return (
		<span className="dsv-compare__option">
			<span className="dsv-compare__option-label">{option.label}</span>
			<StatusBadge status={option.status} />
		</span>
	);
}

VersionLabel.propTypes = {
	option: PropTypes.shape({
		label: PropTypes.string,
		status: PropTypes.string,
	}).isRequired,
};

function VersionSelect({ label, value, options, onChange, testId }) {
	// Derive a stable id base from testId, or a per-instance fallback seed.
	const fallbackBase = useRef(null);
	if (fallbackBase.current === null) {
		instanceSeq += 1;
		fallbackBase.current = `dsv-compare-select-${instanceSeq}`;
	}
	const base = testId || fallbackBase.current;
	const labelId = `${base}-label`;

	const findOption = (optionValue) => options.find((opt) => opt.value === optionValue);
	// value not in options -> fall back to the first option (if any).
	const selected = findOption(value) ?? options[0];

	return (
		<div className="dsv-compare__select">
			<span className="dsv-compare__select-label" id={labelId}>{label}</span>
			<Select
				data-testid={testId}
				aria-labelledby={labelId}
				value={selected?.value ?? null}
				onChange={(next) => onChange(next)}
				disabled={options.length === 0}
				placeholder="—"
				popupMatchSelectWidth={false}
				optionRender={(item) => <VersionLabel option={findOption(item.value)} />}
				labelRender={(item) => {
					const option = findOption(item.value);
					return option ? <VersionLabel option={option} /> : item.label;
				}}
			>
				<SelectContent>
					{options.map((opt) => (
						<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
}

VersionSelect.propTypes = {
	label: PropTypes.string,
	value: PropTypes.string,
	options: PropTypes.arrayOf(PropTypes.shape({
		value: PropTypes.string,
		label: PropTypes.string,
		status: PropTypes.string,
	})),
	onChange: PropTypes.func,
	testId: PropTypes.string,
};

VersionSelect.defaultProps = {
	label: '',
	value: '',
	options: [],
	onChange: () => {},
	testId: undefined,
};

export default VersionSelect;
